import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * Modified for Henshaw:
 * The zoom dialog displays ONLY the individual media item
 * that the customer selected from the product gallery.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} [thumbnails] - The zoom-dialog thumbnail list.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #activeIndex = 0;

  /**
   * ==========================================================
   * START - SHOW ONLY SELECTED IMAGE IN ZOOM
   * ==========================================================
   */

  /**
   * Makes only the selected media item visible inside
   * the full-screen zoom dialog.
   *
   * @param {number} index
   */
  showOnlySelectedMedia(index) {
    const { media, thumbnails } = this.refs;

    this.#activeIndex = index;

    media.forEach((mediaItem, mediaIndex) => {
      const isSelected = mediaIndex === index;

      mediaItem.hidden = !isSelected;

      if (isSelected) {
        mediaItem.style.removeProperty('display');
      } else {
        mediaItem.style.setProperty('display', 'none');
      }
    });

    /*
     * Hide ONLY the thumbnail navigation belonging to
     * the full-screen zoom dialog.
     *
     * This does NOT affect the normal product thumbnails
     * underneath the main product image.
     */
    if (thumbnails) {
      const thumbnailContainer =
        thumbnails.closest('.dialog-thumbnails-list-container');

      if (thumbnailContainer instanceof HTMLElement) {
        thumbnailContainer.style.setProperty(
          'display',
          'none',
          'important'
        );
      }

      thumbnails.style.setProperty(
        'display',
        'none',
        'important'
      );
    }
  }

  /**
   * ==========================================================
   * END - SHOW ONLY SELECTED IMAGE IN ZOOM
   * ==========================================================
   */

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - Index of the selected media.
   * @param {PointerEvent} event - Pointer event that opened zoom.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;

    const targetImage = media[index];

    if (!targetImage) {
      return;
    }

    /*
     * Before opening the dialog, strip the zoom gallery
     * down to the one image that was actually selected.
     */
    this.showOnlySelectedMedia(index);

    const openDialog = () => {
      dialog.showModal();

      /*
       * Ensure we always start at the selected image rather
       * than retaining a previous scroll position.
       */
      dialog.scrollTop = 0;

      const zoomGallery =
        dialog.querySelector('.dialog-zoomed-gallery');

      if (zoomGallery instanceof HTMLElement) {
        zoomGallery.scrollTop = 0;
        zoomGallery.scrollLeft = 0;
      }

      this.loadHighResolutionImage(targetImage);
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li, slideshow-slide')
        : null;

    /*
     * Devices without view-transition support simply open
     * the selected image directly.
     */
    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage
    ) {
      openDialog();
      return;
    }

    const itemTransitionName = 'gallery-item-open';

    sourceImage.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    const focalPoint = sourceImage.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    await startViewTransition(() => {
      openDialog();

      sourceImage.style.removeProperty(
        'view-transition-name'
      );

      targetImage.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );
    });

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );

    targetImage.style.removeProperty(
      'view-transition-name'
    );
  }

  /**
   * Loads the full-resolution image for the selected
   * zoom media item.
   *
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (
      !mediaContainer.classList.contains(
        'product-media-container--image'
      )
    ) {
      return false;
    }

    const image =
      mediaContainer.querySelector(
        'img.product-media__image'
      );

    if (
      !image ||
      !(image instanceof HTMLImageElement)
    ) {
      return false;
    }

    const highResolutionUrl =
      image.getAttribute('data_max_resolution');

    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl)
    ) {
      return false;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();

    newImage.className = image.className;
    newImage.alt = image.alt;

    newImage.setAttribute(
      'data_max_resolution',
      highResolutionUrl
    );

    newImage.setAttribute(
      'ref',
      'image'
    );

    newImage.onload = () => {
      image.replaceWith(newImage);

      this.#highResImagesLoaded.add(
        highResolutionUrl
      );
    };

    newImage.src = highResolutionUrl;

    return true;
  }

  /**
   * Closes the zoom dialog.
   *
   * The close transition returns to the exact gallery
   * image which originally opened the dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    const targetImage =
      media[this.#activeIndex];

    if (!targetImage) {
      this.closeDialog();
      return;
    }

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice()
    ) {
      this.closeDialog();
      return;
    }

    const mediaGallery =
      /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest('media-gallery')
      );

    const slideshowActive =
      mediaGallery?.presentation === 'carousel';

    const slide =
      slideshowActive
        ? mediaGallery?.slideshow?.slides?.[
            this.#activeIndex
          ]
        : mediaGallery?.media?.[
            this.#activeIndex
          ];

    if (!slide) {
      this.closeDialog();
      return;
    }

    const focalPoint =
      slide.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    const itemTransitionName =
      'gallery-item-close';

    targetImage.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    await startViewTransition(() => {
      targetImage.style.removeProperty(
        'view-transition-name'
      );

      slide.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );

      this.closeDialog();
    });

    slide.style.removeProperty(
      'view-transition-name'
    );

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );
  }

  /**
   * Actually closes the native dialog.
   */
  closeDialog() {
    const { dialog } = this.refs;

    dialog.close();

    window.dispatchEvent(
      new DialogCloseEvent()
    );
  }

  /**
   * Closes the dialog when Escape is pressed.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();

    this.close();
  }

  /**
   * These handlers remain available because the Liquid
   * zoom markup still contains the original event bindings.
   * The zoom thumbnails themselves are hidden by this file.
   */

  async handleThumbnailClick(index) {
    this.showOnlySelectedMedia(index);

    const targetImage =
      this.refs.media[index];

    if (targetImage) {
      this.loadHighResolutionImage(
        targetImage
      );
    }
  }

  async handleThumbnailPointerEnter(index) {
    const targetImage =
      this.refs.media[index];

    if (!targetImage) {
      return;
    }

    this.loadHighResolutionImage(
      targetImage
    );
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define(
    'zoom-dialog',
    ZoomDialog
  );
}